import { Home, Video, Dumbbell, ClipboardList, Plus, Loader2 } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { useActiveV3Pipeline } from "@/hooks/useActiveV3Pipeline";
import { useToast } from "@/hooks/use-toast";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

// PLA-138: Bottom nav with Home, Videos, Drills, Training
// PLA-143: Added Upload button for V3 pipeline
const leftNavItems = [
  { id: "home", label: "Home", icon: Home, path: "/" },
  { id: "videos", label: "Videos", icon: Video, path: "/videos" },
];

const rightNavItems = [
  { id: "drills", label: "Drills", icon: Dumbbell, path: "/drills" },
  { id: "training", label: "Training", icon: ClipboardList, path: "/training-program" },
];

// PLA-143: V3 Upload FAB Button
function UploadFab() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  // PLA-143: Track active V3 pipeline for upload button state
  const { 
    uploadDisabled, 
    disabledCopy,
    hasActivePipeline 
  } = useActiveV3Pipeline();

  // Check if we're on the upload page
  const isActive = location.pathname === '/mvp/upload';

  // PLA-143: V3 Upload entry point - navigates to unified V3 upload flow
  const handleV3Upload = () => {
    if (uploadDisabled) {
      toast({
        title: "Upload in progress",
        description: disabledCopy,
      });
      return;
    }
    // Navigate to V3 upload flow
    navigate('/mvp/upload');
  };

  return (
    <div className="relative flex flex-col items-center px-2">
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={handleV3Upload}
            disabled={uploadDisabled}
            className={cn(
              "-mt-5 w-12 h-12 rounded-full",
              "bg-gradient-to-r from-[#F9C94E] to-[#A7F3A1]",
              "shadow-lg hover:shadow-xl",
              "flex items-center justify-center",
              "transition-all duration-200",
              uploadDisabled ? "opacity-50 cursor-not-allowed" : "hover:scale-105",
              isActive && "ring-2 ring-primary ring-offset-2"
            )}
            aria-label={uploadDisabled ? disabledCopy : "Upload video"}
          >
            {hasActivePipeline ? (
              <Loader2 className="w-6 h-6 text-[#0D382A] animate-spin" />
            ) : (
              <Plus className="w-6 h-6 text-[#0D382A]" />
            )}
          </button>
        </TooltipTrigger>
        {uploadDisabled && (
          <TooltipContent side="top" className="max-w-xs">
            <p>{disabledCopy}</p>
          </TooltipContent>
        )}
      </Tooltip>
      <span className={cn(
        "text-xs font-medium mt-1",
        isActive ? "text-primary" : "text-muted-foreground"
      )}>
        Upload
      </span>
    </div>
  );
}

export function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();

  const renderNavItem = (item: typeof leftNavItems[0]) => {
    // Check if current path matches or starts with the nav item path
    const isActive = item.path === '/videos' 
      ? location.pathname === '/videos' || location.pathname.startsWith('/analysis/')
      : item.path === '/drills'
      ? location.pathname === '/drills'
      : item.path === '/training-program'
      ? location.pathname.startsWith('/training-program') || location.pathname.startsWith('/snapshot') || location.pathname.startsWith('/practice')
      : location.pathname === item.path;
    const Icon = item.icon;
    
    return (
      <button
        key={item.id}
        onClick={() => navigate(item.path)}
        className={cn("tab-item", isActive && "active")}
      >
        <div className="relative flex flex-col items-center">
          <Icon size={20} className="mb-1" />
          {isActive && (
            <motion.div
              layoutId="mobile-nav-indicator"
              className="absolute -bottom-2 w-1 h-1 bg-primary rounded-full"
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            />
          )}
        </div>
        <span className="text-xs font-medium">{item.label}</span>
      </button>
    );
  };

  return (
    <nav className="bottom-tab-bar">
      {/* Left nav items */}
      {leftNavItems.map(renderNavItem)}
      
      {/* PLA-143: Center Upload FAB */}
      <UploadFab />
      
      {/* Right nav items */}
      {rightNavItems.map(renderNavItem)}
    </nav>
  );
}